/** Collapsible primitive. */
import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Props for {@link Collapsible}. */
interface CollapsibleProps {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}

/** Titled panel that expands and collapses its children. */
export const Collapsible: React.FC<CollapsibleProps> = ({
  title,
  subtitle,
  defaultOpen = true,
  children,
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div
      className={cn(
        'rounded-2xl border bg-card transition-all',
        'border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)]',
        'hover:border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-[color-mix(in_srgb,var(--foreground)_3%,transparent)] transition-colors rounded-t-2xl"
      >
        <div className="flex items-center gap-3 text-left">
          <h3 className="text-base font-semibold text-[var(--color-2-nuance-1)]">{title}</h3>
          {subtitle && <span className="text-sm text-muted-foreground">{subtitle}</span>}
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-muted-foreground transition-transform duration-200 shrink-0',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300',
          isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="p-4 pt-0">{children}</div>
      </div>
    </div>
  );
};
